/**
 * ToastContext — thin wrapper around `@archieai/ui`'s imperative `showToast`
 * helpers (Sonner-backed). The local `Toast`/`ToastContainer` primitives have
 * been replaced by the upstream Archie UI Toast (Phase 1C cutover).
 *
 * The public API (`useToast()` returning `success`, `error`, `warning`, `info`,
 * `dismiss`, `dismissAll`) is preserved so existing call-sites keep working.
 * The `toasts` field is retained for back-compat but is no longer used to
 * render toasts — the upstream Sonner portal handles that. It always returns
 * an empty array.
 */
import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { showToast } from '@archieai/ui';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  /** Deprecated — kept for back-compat. The upstream Sonner portal renders
   *  toasts directly, so this list is always empty. */
  toasts: Toast[];
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 5000;
const ERROR_DEFAULT_DURATION = 8000;

const TYPE_TO_HELPER: Record<ToastType, (title: string, description?: string) => string | number> = {
  success: showToast.success,
  error: showToast.error,
  warning: showToast.warning,
  info: showToast.info,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  // Track active toast ids so callers can still call dismiss(id) / dismissAll().
  const activeIdsRef = useRef<Set<string | number>>(new Set());

  const dismiss = useCallback((id: string) => {
    showToast.dismiss(id);
    activeIdsRef.current.delete(id);
  }, []);

  const dismissAll = useCallback(() => {
    showToast.dismiss();
    activeIdsRef.current.clear();
  }, []);

  const fire = useCallback(
    (type: ToastType, message: string, duration?: number) => {
      const id = TYPE_TO_HELPER[type](message);
      activeIdsRef.current.add(id);

      const ttl = duration ?? (type === 'error' ? ERROR_DEFAULT_DURATION : DEFAULT_DURATION);
      if (ttl > 0) {
        setTimeout(() => {
          showToast.dismiss(id);
          activeIdsRef.current.delete(id);
        }, ttl);
      }

      return String(id);
    },
    [],
  );

  const success = useCallback(
    (message: string, duration?: number) => fire('success', message, duration),
    [fire],
  );
  const error = useCallback(
    (message: string, duration?: number) => fire('error', message, duration),
    [fire],
  );
  const warning = useCallback(
    (message: string, duration?: number) => fire('warning', message, duration),
    [fire],
  );
  const info = useCallback(
    (message: string, duration?: number) => fire('info', message, duration),
    [fire],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts: [], success, error, warning, info, dismiss, dismissAll }),
    [success, error, warning, info, dismiss, dismissAll],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
